/**
 * Phone axis labels. d3 hands back 5-10 ticks whatever the axis width, which
 * a ~375 px axis cannot hold: 72 px labels land ~50 px apart and smear into
 * each other. These helpers pick the smallest calendar step whose labels fit
 * side by side, and nudge a label next to an edge inward so it is not cut off.
 */

import {
  type CountableTimeInterval,
  type TimeInterval,
  timeDay,
  timeHour,
  timeMinute,
  timeMonth,
  timeSecond,
  timeWeek,
  timeYear,
} from 'd3'

/** Glyph advance of the 10 px mono tick label, rounded up for safety. */
const TICK_CHAR_PX = 6.2
/** Clear space kept between two neighbouring labels. */
const TICK_LABEL_GAP_PX = 8

const EPOCH = new Date(0)

function steps(unit: CountableTimeInterval, counts: readonly number[]): TimeInterval[] {
  return counts.flatMap(count => unit.every(count) ?? [])
}

/** Every `count`-th day counted from the epoch — d3's own `every` restarts each month (…29, 31, 01, 03…). */
function everyDays(count: number): TimeInterval {
  return timeDay.filter(day => timeDay.count(EPOCH, day) % count === 0)
}

/**
 * Calendar steps, finest first. Denser than d3's own table (which jumps 2 days
 * → 1 week → 1 month → 3 months → 1 year) so a narrow axis never drops from
 * "too many labels" straight to a single one.
 */
const TICK_STEPS: readonly TimeInterval[] = [
  ...steps(timeSecond, [1, 2, 5, 10, 15, 30]),
  ...steps(timeMinute, [1, 2, 5, 10, 15, 30]),
  ...steps(timeHour, [1, 2, 3, 6, 12]),
  timeDay,
  everyDays(2),
  everyDays(4),
  ...steps(timeWeek, [1, 2]),
  ...steps(timeMonth, [1, 3, 6]),
  ...steps(timeYear, [1, 2, 5, 10, 25, 50, 100]),
]

export interface TickAxis {
  /** Pixel position of a tick on the axis. */
  x: (tick: Date) => number
  /** Axis width in px. */
  width: number
  /** The text drawn under a tick. */
  label: (tick: Date) => string
}

interface LabelSpan {
  tick: Date
  /** Whether the label had to be nudged away from an edge. */
  shifted: boolean
  left: number
  right: number
}

/** Estimated rendered width of a tick's label. */
function labelWidth(tick: Date, axis: TickAxis): number {
  return axis.label(tick).length * TICK_CHAR_PX
}

/**
 * Horizontal nudge (px) that keeps a tick's centred label inside the axis:
 * 0 when it has room on both sides, positive near the left edge, negative
 * near the right one.
 */
export function tickLabelShift(tick: Date, axis: TickAxis): number {
  const x = axis.x(tick)
  const half = labelWidth(tick, axis) / 2
  if (x < half) return half - x
  if (x > axis.width - half) return axis.width - half - x
  return 0
}

function labelSpan(tick: Date, axis: TickAxis): LabelSpan {
  const shift = tickLabelShift(tick, axis)
  const width = labelWidth(tick, axis)
  const left = axis.x(tick) + shift - width / 2
  return { tick, shifted: shift !== 0, left, right: left + width }
}

function clearOf(a: LabelSpan, b: LabelSpan): boolean {
  return a.left >= b.right + TICK_LABEL_GAP_PX || a.right <= b.left - TICK_LABEL_GAP_PX
}

/**
 * The ticks whose labels fit without touching each other. Labels that sit
 * on their tick win over the ones nudged inward at an edge, so the cadence of
 * the axis stays regular and only the odd edge label is left out.
 */
export function spacedTicks(ticks: readonly Date[], axis: TickAxis): Date[] {
  const spans = ticks
    .filter(tick => axis.x(tick) >= 0 && axis.x(tick) <= axis.width)
    .map(tick => labelSpan(tick, axis))
  const byPriority = [...spans.filter(s => !s.shifted), ...spans.filter(s => s.shifted)]
  const kept: LabelSpan[] = []
  for (const span of byPriority) {
    if (kept.every(other => clearOf(span, other))) kept.push(span)
  }
  return kept.sort((a, b) => a.left - b.left).map(s => s.tick)
}

/** Distance in px between two neighbouring ticks of `step`, measured at `from`. */
function stepPx(step: TimeInterval, from: Date, x: TickAxis['x']): number {
  const first = step.ceil(from)
  return x(step.offset(first, 1)) - x(first)
}

/**
 * Whether every label of `ticks` has room next to its neighbour and says
 * something new. Months and DST days differ in length, so the real gaps are
 * checked; a step finer than the label format (two ticks both reading
 * "Jan 2021") is turned down as well.
 */
function labelsFit(ticks: readonly Date[], axis: TickAxis): boolean {
  for (let i = 1; i < ticks.length; i++) {
    const gap = axis.x(ticks[i]) - axis.x(ticks[i - 1])
    if (gap < labelWidth(ticks[i], axis) + TICK_LABEL_GAP_PX) return false
    if (axis.label(ticks[i]) === axis.label(ticks[i - 1])) return false
  }
  return true
}

/**
 * Ticks for a narrow axis showing `startMs`..`endMs`: the smallest calendar
 * step whose labels fit. The steps are anchored to the calendar, not to the
 * visible window, so the labels keep an even cadence and do not jump while
 * the axis is panned.
 */
export function fittingTicks(startMs: number, endMs: number, axis: TickAxis): Date[] {
  const start = new Date(startMs)
  const stop = new Date(endMs + 1)
  const need = labelWidth(start, axis) + TICK_LABEL_GAP_PX
  for (const step of TICK_STEPS) {
    // Cheap test first: a step far too fine for the window would otherwise
    // be expanded into millions of dates.
    if (stepPx(step, start, axis.x) < need) continue
    const ticks = step.range(start, stop)
    if (labelsFit(ticks, axis)) return spacedTicks(ticks, axis)
  }
  return spacedTicks(TICK_STEPS[TICK_STEPS.length - 1].range(start, stop), axis)
}
